class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelectorAll(".cart-open-trigger");
    cartLink.forEach((links) => {
      links.setAttribute("role", "button");
      links.setAttribute("aria-haspopup", "dialog");
      links.addEventListener("click", (event) => {
        event.preventDefault();
        this.open(links);
      });
      links.addEventListener("keydown", (event) => {
        if (event.code.toUpperCase() === "SPACE") {
          event.preventDefault();
          this.open(links);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
      {
        id: "cart-icon-bubble-mobile",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerUpsells extends HTMLElement {
  constructor() {
    super();
    this.drawer = document.querySelector("cart-drawer");
    this.swiper = null;
    this.bindEvents();
    this.initUpsellsSwiper();
  }

  bindEvents() {
    document.addEventListener("click", (e) => {
      const btn = e.target.closest(".upsell-card__add-btn");
      if (btn && !btn.disabled) this.handleAddToCart(btn);
    });
  }

  handleAddToCart(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add("loading");
    btn.disabled = true;

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: this.getSectionsToRender().map((s) => s.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          this.handleError(btn, data.description);
          return;
        }
        this.renderSections(data.sections);
      })
      .catch(() => this.handleError(btn, window.cartStrings?.error || "Error"))
      .finally(() => {
        btn.classList.remove("loading");
        btn.disabled = false;
      });
  }

  getSectionsToRender() {
    return [
      { id: "cart-drawer", selector: "#CartDrawer" },
      { id: "cart-icon-bubble" },
      { id: "cart-icon-bubble-mobile" },
    ];
  }

  renderSections(sections) {
    this.getSectionsToRender().forEach((section) => {
      const el = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!el || !sections[section.id]) return;

      const html = new DOMParser().parseFromString(
        sections[section.id],
        "text/html"
      );
      const source = section.selector
        ? html.querySelector(section.selector)
        : html.querySelector(".shopify-section");
      if (source) el.innerHTML = source.innerHTML;
    });

    // Re-bind overlay click after render
    const overlay = document.getElementById("CartDrawer-Overlay");
    if (overlay && this.drawer) {
      overlay.addEventListener("click", this.drawer.close.bind(this.drawer));
    }

    // Re-init swiper after render
    this.initUpsellsSwiper();
  }

  initUpsellsSwiper() {
    if (typeof Swiper === "undefined") return;

    const swiperEl = document.querySelector(
      ".drawer__upsells-carousel--swiper-wrapper"
    );
    if (!swiperEl) return;

    // Destroy existing swiper if present
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.swiper = new Swiper(".drawer__upsells-carousel--swiper-wrapper", {
      slidesPerView: 2.25,
      spaceBetween: 8,
      slidesOffsetBefore: 20,
      slidesOffsetAfter: 20,
      grabCursor: true,
      watchSlidesProgress: true,
      navigation: {
        nextEl: ".drawer__upsells-carousel--swiper-arrow .swiper-button-next",
        prevEl: ".drawer__upsells-carousel--swiper-arrow .swiper-button-prev",
      },
      breakpoints: {
        750: {
          slidesPerView: 3.5,
          spaceBetween: 16,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
        },
      },
    });
  }

  handleError(btn, message) {
    console.error("Upsell add to cart error:", message);
    btn.classList.remove("loading");
    btn.disabled = false;
  }
}

customElements.define("cart-drawer-upsells", CartDrawerUpsells);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-icon-bubble-mobile",
        section: "cart-icon-bubble-mobile",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
